/* eslint-env browser */
// Driver Reset tab — extracted from the legacy SecurityDashboard (wave 3:
// Dashboard tiles, the modern table, Addis 12-hour times, the shared
// confirmation dialog).
//
// Manual trigger UI for the daily midnight driver reset Cloud Function
// plus a log of recent runs. Both data fetch + function call are
// deferred until admin opens the tab. The call is unchanged: POST
// <host>/triggerDriverReset with the admin's ID token, after a confirmation.

import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, query, limitToLast } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DashboardCard from '../../components/DashboardCard';
import CircularLoading from '../../components/CircularLoading';
import { confirmDialog, InlineMessage, EmptyState } from '../../components/feedback';
import { DataTable, StatusChip, formatNumber } from '../../components/modern';
import { tokens as c } from '../../theme';

// The button calls the function through the Hosting rewrite, which answers
// 504 after 60 s while the function (timeout 300 s) keeps running and still
// writes its log row. How long to keep the button disabled before re-reading
// the log table in that case.
const STILL_RUNNING_RECHECK_MS = 75000;

export default function DriverReset() {
  const { t } = useTranslation();
  const { database, auth: firebaseAuth, config } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settingsData = useSelector((state) => state.settingsdata);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [driverResetLogs, setDriverResetLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggeringReset, setTriggeringReset] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  // One run per click: set for the whole life of a run, including the wait
  // after a gateway timeout (state alone lags a fast double click).
  const resetInFlight = useRef(false);
  const recheckTimer = useRef(null);

  useEffect(
    () => () => {
      if (recheckTimer.current) clearTimeout(recheckTimer.current);
    },
    []
  );

  const fetchResetLogs = async () => {
    if (!database) return [];
    const logsQuery = query(ref(database, 'driver_reset_logs'), limitToLast(50));
    const snapshot = await get(logsQuery);
    const data = snapshot.val();
    if (data) {
      const logsArray = Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
      }));
      logsArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setDriverResetLogs(logsArray);
      setLoading(false);
      return logsArray;
    }
    setDriverResetLogs([]);
    setLoading(false);
    return [];
  };

  useEffect(() => {
    if (!database || !isAdmin) return;
    fetchResetLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [database, isAdmin]);

  const handleTriggerDriverReset = async () => {
    if (resetInFlight.current) return;
    resetInFlight.current = true;
    setTriggeringReset(true);
    setResetResult(null);
    const startedAt = Date.now();
    let stillRunning = false;

    try {
      const currentUser = firebaseAuth.currentUser;
      if (!currentUser) {
        setResetResult({ error: 'Not authenticated' });
        setTriggeringReset(false);
        return;
      }

      const idToken = await currentUser.getIdToken();
      const settings = settingsData?.settings;
      const host =
        typeof window !== 'undefined' && window.location && settings?.CompanyWebsite === window.location.origin
          ? window.location.origin
          : `https://${config?.projectId || 'donkey-drive-5cdd1'}.web.app`;

      const response = await fetch(`${host}/triggerDriverReset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });

      // A gateway answer (Hosting 504 / 502 / 503, an HTML body) is not the
      // function's own reply: the run is still going and will write its log row.
      let result = null;
      try {
        result = await response.json();
      } catch (_) {
        result = null;
      }
      const gatewayStatus = response.status === 502 || response.status === 503 || response.status === 504;

      if (response.ok && result) {
        const problems = Array.isArray(result.errors) ? result.errors.length : 0;
        setResetResult({
          success: true,
          message:
            t('driver_reset_done', {
              activated: result.driversActivated,
              queue: result.driversQueueReset,
              skipped: result.nonCompliant,
              defaultValue: 'Activated {{activated}} drivers, reset queue for {{queue}} drivers ({{skipped}} non-compliant skipped)',
            }) + (problems ? ` — ${t('driver_reset_problems', { count: problems, defaultValue: '{{count}} write problem(s), see log' })}` : ''),
        });
        fetchResetLogs();
      } else if (result && !gatewayStatus) {
        // The function itself refused or failed (401 / 403 / 500 + { error }).
        setResetResult({ error: result.error || t('driver_reset_failed', { defaultValue: 'Reset failed' }) });
      } else {
        // Keep the button disabled (no second concurrent run from this click)
        // and re-read the log table once the run has had time to finish.
        stillRunning = true;
        setResetResult({ pending: true, message: t('driver_reset_still_running', { defaultValue: 'Reset is still running — the log table will update' }) });
        recheckTimer.current = setTimeout(async () => {
          recheckTimer.current = null;
          let logs = [];
          try {
            logs = await fetchResetLogs();
          } catch (_) {
            /* keep the table as it is */
          }
          // One minute of slack for clock skew between this browser and the server.
          const landed = logs.some((l) => (l.timestamp || 0) >= startedAt - 60000);
          setResetResult(
            landed
              ? { success: true, message: t('driver_reset_finished', { defaultValue: 'Reset finished — see the newest row in the log table' }) }
              : {
                  pending: true,
                  message: t('driver_reset_maybe_finishing', {
                    defaultValue: 'Reset may still be finishing — reopen this tab in a minute to see its log row before running it again',
                  }),
                }
          );
          resetInFlight.current = false;
          setTriggeringReset(false);
        }, STILL_RUNNING_RECHECK_MS);
      }
    } catch (error) {
      console.error('Driver reset failed:', error);
      setResetResult({ error: error.message });
    } finally {
      if (!stillRunning) {
        resetInFlight.current = false;
        setTriggeringReset(false);
      }
    }
  };

  const askAndRun = async () => {
    const ok = await confirmDialog({
      title: t('driver_reset_confirm_title', { defaultValue: 'Run the driver reset now?' }),
      body: t('driver_reset_confirm_body', {
        defaultValue:
          'This will activate all compliant drivers and reset queue status for drivers without active bookings. Compliant = approved AND passes the onboarding step table (the same rule as the driver-home switch: a car type — placeholders count — plus whatever steps the table marks Required). Never sets anyone inactive.',
      }),
      confirmLabel: t('driver_reset_run_now', { defaultValue: 'Run Now' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
    });
    if (!ok) {
      setResetResult(null);
      return;
    }
    handleTriggerDriverReset();
  };

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  if (loading) return <CircularLoading inline />;

  const totalRuns = driverResetLogs.length;
  const totalActivated = driverResetLogs.reduce((sum, l) => sum + (l.driversActivated || 0), 0);
  const totalQueueReset = driverResetLogs.reduce((sum, l) => sum + (l.driversQueueReset || 0), 0);
  const totalNonCompliant = driverResetLogs.reduce((sum, l) => sum + (l.nonCompliant || 0), 0);
  const quiet = (text) => (
    <Box component="span" sx={{ color: c.TEXT_3 }}>
      {text}
    </Box>
  );

  const columns = [
    { title: t('date', { defaultValue: 'Date' }), field: 'timestamp', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('driver_reset_triggered_by', { defaultValue: 'Triggered By' }),
      field: 'triggeredBy',
      type: 'status',
      sortValue: (l) => (l.triggeredBy === 'scheduled' ? 0 : 1),
      exportValue: (l) => (l.triggeredBy === 'scheduled' ? 'Scheduled' : 'Manual'),
      status: (l) =>
        l.triggeredBy === 'scheduled'
          ? { tone: 'neutral', icon: <ScheduleRoundedIcon />, label: t('scheduled', { defaultValue: 'Scheduled' }) }
          : { tone: 'info', icon: <TouchAppOutlinedIcon />, label: t('driver_reset_manual', { defaultValue: 'Manual' }) },
    },
    { title: t('total_drivers', { defaultValue: 'Total Drivers' }), field: 'totalDrivers', type: 'number', render: (l) => formatNumber(l.totalDrivers || 0) },
    {
      title: t('driver_reset_activated', { defaultValue: 'Activated' }),
      field: 'driversActivated',
      type: 'number',
      render: (l) => ((l.driversActivated || 0) > 0 ? <StatusChip tone="success" label={formatNumber(l.driversActivated)} /> : quiet('0')),
    },
    {
      title: t('driver_reset_queue_reset', { defaultValue: 'Queue Reset' }),
      field: 'driversQueueReset',
      type: 'number',
      render: (l) => ((l.driversQueueReset || 0) > 0 ? <StatusChip tone="warning" label={formatNumber(l.driversQueueReset)} /> : quiet('0')),
    },
    { title: t('driver_reset_non_compliant', { defaultValue: 'Non-Compliant' }), field: 'nonCompliant', type: 'number', render: (l) => formatNumber(l.nonCompliant || 0) },
    {
      id: 'errors',
      title: t('errors', { defaultValue: 'Errors' }),
      type: 'number',
      sortValue: (l) => (l.errors || []).length,
      exportValue: (l) => (l.errors || []).length,
      render: (l) =>
        (l.errors || []).length > 0 ? (
          <Tooltip title={(l.errors || []).join(', ')}>
            <span>
              <StatusChip tone="error" label={formatNumber(l.errors.length)} />
            </span>
          </Tooltip>
        ) : (
          quiet('0')
        ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <InlineMessage kind="info" sx={{ flex: '1 1 320px' }}>
          {t('driver_reset_info', {
            defaultValue: 'Midnight driver reset activates compliant drivers and clears stale queue flags. Runs every day at 12:00 am (Addis Ababa time).',
          })}
        </InlineMessage>
        <Button
          variant="contained"
          startIcon={triggeringReset ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
          disabled={triggeringReset}
          onClick={askAndRun}
          sx={{ minWidth: 200 }}
        >
          {triggeringReset ? t('driver_reset_running', { defaultValue: 'Running...' }) : t('driver_reset_run', { defaultValue: 'Run Driver Reset Now' })}
        </Button>
      </Box>

      {resetResult ? (
        <InlineMessage kind={resetResult.success ? 'success' : resetResult.pending ? 'info' : 'error'} onClose={() => setResetResult(null)}>
          {resetResult.success || resetResult.pending ? resetResult.message : resetResult.error}
        </InlineMessage>
      ) : null}

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
        <DashboardCard icon={<AutorenewRoundedIcon />} hue="accent" title={t('driver_reset_total_runs', { defaultValue: 'Total Runs' })} value={formatNumber(totalRuns)} />
        <DashboardCard icon={<CheckCircleOutlineRoundedIcon />} hue="success" title={t('driver_reset_drivers_activated', { defaultValue: 'Drivers Activated' })} value={formatNumber(totalActivated)} />
        <DashboardCard icon={<SpeedOutlinedIcon />} hue="amber" title={t('driver_reset_queue_reset', { defaultValue: 'Queue Reset' })} value={formatNumber(totalQueueReset)} />
        <DashboardCard icon={<WarningAmberRoundedIcon />} hue="danger" title={t('driver_reset_non_compliant_skipped', { defaultValue: 'Non-Compliant Skipped' })} value={formatNumber(totalNonCompliant)} />
      </Box>

      <DataTable
        title={t('security_tab_driver_reset', { defaultValue: 'Driver Reset' })}
        rows={driverResetLogs}
        columns={columns}
        rowKey="id"
        countText={({ total }) =>
          t('driver_reset_runs_count', { count: total, defaultValue_one: '{{count}} run (last 50)', defaultValue_other: '{{count}} runs (last 50)', defaultValue: '{{count}} runs (last 50)' })
        }
        search={false}
        prefsKey="security_driver_reset"
        minWidth={820}
        emptyState={{ icon: <AutorenewRoundedIcon />, title: t('driver_reset_empty', { defaultValue: 'No driver reset runs yet' }) }}
      />
    </Box>
  );
}
